import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import { companyApiService } from '../../services/companyApi';
import { useFlowProtection } from '../../../shared/hooks/useFlowProtection';

// Industry enum matching backend (must match Python Enum values EXACTLY as backend expects)
const INDUSTRIES = [
    // B2B/General
    { value: 'B2B', label: 'B2B' },
    { value: 'Analytics', label: 'Analytics' },
    { value: 'Engineering, Product and Design', label: 'Engineering, Product and Design' },
    { value: 'Finance and Accounting', label: 'Finance and Accounting' },
    { value: 'Human Resources', label: 'Human Resources' },
    { value: 'Infrastructure', label: 'Infrastructure' },
    { value: 'Legal', label: 'Legal' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Office Management', label: 'Office Management' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Productivity', label: 'Productivity' },
    { value: 'Recruiting and Talent', label: 'Recruiting and Talent' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Sales', label: 'Sales' },
    { value: 'Security', label: 'Security' },
    { value: 'Supply Chain and Logistics', label: 'Supply Chain and Logistics' },
    // Fintech
    { value: 'Fintech', label: 'Fintech' },
    { value: 'Asset Management', label: 'Asset Management' },
    { value: 'Banking and Exchange', label: 'Banking and Exchange' },
    { value: 'Consumer Finance', label: 'Consumer Finance' },
    { value: 'Credit and Lending', label: 'Credit and Lending' },
    { value: 'Insurance', label: 'Insurance' },
    { value: 'Payments', label: 'Payments' },
    // Consumer
    { value: 'Consumer', label: 'Consumer' },
    { value: 'Apparel and Cosmetics', label: 'Apparel and Cosmetics' },
    { value: 'Consumer Electronics', label: 'Consumer Electronics' },
    { value: 'Content', label: 'Content' },
    { value: 'Food and Beverage', label: 'Food and Beverage' },
    { value: 'Gaming', label: 'Gaming' },
    { value: 'Home and Personal', label: 'Home and Personal' },
    { value: 'Job and Career Services', label: 'Job and Career Services' },
    { value: 'Social', label: 'Social' },
    { value: 'Transportation Services', label: 'Transportation Services' },
    { value: 'Travel, Leisure and Tourism', label: 'Travel, Leisure and Tourism' },
    { value: 'Virtual and Augmented Reality', label: 'Virtual and Augmented Reality' },
    // Other
    { value: 'Education', label: 'Education' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Real Estate and Construction', label: 'Real Estate and Construction' },
    { value: 'Industrials', label: 'Industrials' },
    { value: 'Manufacturing and Robotics', label: 'Manufacturing and Robotics' },
    { value: 'Government', label: 'Government' },
    { value: 'Unspecified', label: 'Unspecified' },
];

// Organization Size enum matching backend (must match Python Enum values EXACTLY as backend expects)
const ORGANIZATION_SIZES = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-100', label: '11-100 employees' },
    { value: '101-300', label: '101-300 employees' },
    { value: '301+', label: '301+ employees' },
];

const CompanyProfileSetup: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Flow protection - only accessible after company signup flow
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromCompanySignup',
        fallbackPath: '/auth/company/signup'
    });

    // Get user info from navigation state OR localStorage (for login redirect)
    const getUserInfo = () => {
        // First try navigation state (from signup flow)
        if (location.state?.email) {
            return {
                email: location.state.email,
                firstName: location.state.firstName || '',
                lastName: location.state.lastName || ''
            };
        }
        // Otherwise get from localStorage (from login redirect)
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                return {
                    email: userData.email || '',
                    firstName: userData.firstName || userData.first_name || '',
                    lastName: userData.lastName || userData.last_name || ''
                };
            } catch {
                return { email: '', firstName: '', lastName: '' };
            }
        }
        return { email: '', firstName: '', lastName: '' };
    };

    const userInfo = getUserInfo();
    const userEmail = userInfo.email;
    const firstName = userInfo.firstName;
    const lastName = userInfo.lastName;

    const [formData, setFormData] = useState({
        name: '',
        website: '',
        industry: '',
        organizationSize: '',
        description: '',
    });

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setErrors(prev => ({ ...prev, logo: 'Only JPEG, PNG, GIF, and WebP images are allowed' }));
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                setErrors(prev => ({ ...prev, logo: 'File size must be less than 5MB' }));
                return;
            }

            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
            setErrors(prev => ({ ...prev, logo: '' }));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Company name is required';
        }

        if (!formData.website.trim()) {
            newErrors.website = 'Website is required';
        } else if (!formData.website.match(/^https?:\/\/.+/)) {
            newErrors.website = 'Website URL must start with http:// or https://';
        }

        if (!formData.industry) {
            newErrors.industry = 'Industry is required';
        }

        if (!formData.organizationSize) {
            newErrors.organizationSize = 'Organization size is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            // Call Company-Service API via companyApiService
            const result = await companyApiService.createCompany({
                name: formData.name,
                website: formData.website || '',
                industry: formData.industry,
                companySize: formData.organizationSize,
                description: formData.description || '',
            });

            console.log('Company created successfully:', result);

            // Store company data in localStorage for later use (invitations, dashboard, etc.)
            if (result.companyId) {
                const companyInfo = {
                    id: result.companyId,
                    name: formData.name,
                    industry: formData.industry,
                    organization_size: formData.organizationSize,
                    website: formData.website,
                    logo_url: null // Logo upload not supported yet
                };
                localStorage.setItem('companyData', JSON.stringify(companyInfo));
            }

            // Update authUser with profileCompleted = true to prevent redirect loop on next login
            const authUserStr = localStorage.getItem('authUser');
            if (authUserStr) {
                try {
                    const authUser = JSON.parse(authUserStr);
                    authUser.profileCompleted = true;
                    authUser.profile_completed = true;
                    localStorage.setItem('authUser', JSON.stringify(authUser));
                } catch {
                    // Ignore parsing errors
                }
            }

            // Navigate to invite team page
            navigate('/companies/invite-team', {
                state: {
                    companyName: formData.name
                }
            });

        } catch (err: any) {
            console.error('Company creation failed:', err);
            if (err.message.includes('already exists')) {
                setErrors({ name: 'A company with this name already exists' });
            } else {
                setErrors({ general: err.message || 'Failed to create company profile' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show nothing while checking access (redirect happens in hook if invalid)
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-[597px]">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-12 w-auto"
                        />
                    </div>

                    {/* Header */}
                    <div className="mb-[48px]">
                        <h1
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 700,
                                fontSize: '48px',
                                lineHeight: '100%',
                                color: '#015FA6',
                                marginBottom: '24px'
                            }}
                        >
                            Create your company profile
                        </h1>
                        <p
                            style={{
                                fontFamily: 'SF Pro',
                                fontWeight: 110,
                                fontSize: '18px',
                                lineHeight: '150%',
                                color: '#015FA6'
                            }}
                        >
                            Tell us about your company
                        </p>
                    </div>

                    {/* General Error */}
                    {errors.general && (
                        <div style={{
                            padding: '12px',
                            marginBottom: '24px',
                            background: '#FEE2E2',
                            border: '1px solid #EF4444',
                            borderRadius: '8px',
                            fontFamily: 'Manrope',
                            fontSize: '14px',
                            color: '#991B1B'
                        }}>
                            {errors.general}
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Company Name */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="name"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Company Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Acme Corporation"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: `1px solid ${errors.name ? '#EF4444' : '#CBD5E0'}`,
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    if (!errors.name) e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    if (!errors.name) e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                            {errors.name && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        {/* Website */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="website"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Website
                            </label>
                            <input
                                type="text"
                                id="website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://www.example.com"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: `1px solid ${errors.website ? '#EF4444' : '#CBD5E0'}`,
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    if (!errors.website) e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    if (!errors.website) e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                            {errors.website && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.website}
                                </p>
                            )}
                        </div>

                        {/* Industry and Organization Size - Two Columns */}
                        <div style={{ display: 'flex', gap: '22px', marginBottom: '24px' }}>
                            {/* Industry */}
                            <div style={{ flex: 1 }}>
                                <label
                                    htmlFor="industry"
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    Industry
                                </label>
                                <select
                                    id="industry"
                                    name="industry"
                                    value={formData.industry}
                                    onChange={handleChange}
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: formData.industry ? '#015FA6' : 'rgba(1, 95, 166, 0.5)',
                                        background: '#F7FAFC',
                                        border: `1px solid ${errors.industry ? '#EF4444' : '#CBD5E0'}`,
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px',
                                        cursor: 'pointer'
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.industry) e.target.style.borderColor = '#015FA6';
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.industry) e.target.style.borderColor = '#CBD5E0';
                                    }}
                                >
                                    <option value="">Select Industry</option>
                                    {INDUSTRIES.map(industry => (
                                        <option key={industry.value} value={industry.value}>
                                            {industry.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.industry && (
                                    <p style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#EF4444',
                                        marginTop: '8px'
                                    }}>
                                        {errors.industry}
                                    </p>
                                )}
                            </div>

                            {/* Organization Size */}
                            <div style={{ flex: 1 }}>
                                <label
                                    htmlFor="organizationSize"
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    Company Size
                                </label>
                                <select
                                    id="organizationSize"
                                    name="organizationSize"
                                    value={formData.organizationSize}
                                    onChange={handleChange}
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: formData.organizationSize ? '#015FA6' : 'rgba(1, 95, 166, 0.5)',
                                        background: '#F7FAFC',
                                        border: `1px solid ${errors.organizationSize ? '#EF4444' : '#CBD5E0'}`,
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px',
                                        cursor: 'pointer'
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.organizationSize) e.target.style.borderColor = '#015FA6';
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.organizationSize) e.target.style.borderColor = '#CBD5E0';
                                    }}
                                >
                                    <option value="">Select Size</option>
                                    {ORGANIZATION_SIZES.map(size => (
                                        <option key={size.value} value={size.value}>
                                            {size.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.organizationSize && (
                                    <p style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#EF4444',
                                        marginTop: '8px'
                                    }}>
                                        {errors.organizationSize}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="description"
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Company Description (Optional)
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Tell us about your company..."
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px',
                                    resize: 'vertical'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                        </div>

                        {/* Company Logo */}
                        <div style={{ marginBottom: '48px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Company Logo (Optional)
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                {logoPreview && (
                                    <img
                                        src={logoPreview}
                                        alt="Logo preview"
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            border: '1px solid #CBD5E0'
                                        }}
                                    />
                                )}
                                <label
                                    style={{
                                        display: 'inline-block',
                                        padding: '10px 20px',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#FFFFFF',
                                        border: '1px solid #015FA6',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#F7FAFC';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#FFFFFF';
                                    }}
                                >
                                    Choose File
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                        onChange={handleLogoChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                {logoFile && (
                                    <span style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#67728A'
                                    }}>
                                        {logoFile.name}
                                    </span>
                                )}
                            </div>
                            {errors.logo && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#EF4444',
                                    marginTop: '8px'
                                }}>
                                    {errors.logo}
                                </p>
                            )}
                            <p style={{
                                fontFamily: 'Manrope',
                                fontSize: '12px',
                                color: '#67728A',
                                marginTop: '8px'
                            }}>
                                Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
                            </p>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '529px',
                                maxWidth: '100%',
                                height: '55px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#F7FAFC',
                                background: isLoading ? '#CBD5E0' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) e.currentTarget.style.background = '#014580';
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) e.currentTarget.style.background = '#015FA6';
                            }}
                        >
                            {isLoading ? 'Creating Company...' : 'Create Company Profile'}
                        </button>
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <AuthHero variant="company" />
        </div>
    );
};

export default CompanyProfileSetup;
